/**
 * Monitors the Instagram Feed app block and wraps the handle in a link
 */
const updateInstaFeedLink = () => {
  const targetNode = document.querySelector(
    '.Vtl-InstagramFeedWidget__Title h2'
  );

  // Check if it exists and hasn't been linked yet
  if (targetNode && !targetNode.querySelector('a')) {
    const originalText = targetNode.textContent; // e.g., "FOLLOW US @CODE8BEAUTY"
    const instagramUrl = window.theme_settings?.instagram_url || '#';

    // Replace the text with a link version
    // We split at "FOLLOW US " to keep the "FOLLOW US" part outside the link if preferred,
    // or wrap the whole text. Here we wrap the @handle specifically:
    if (originalText.includes('@')) {
      const parts = originalText.split('@');
      targetNode.innerHTML = `${parts[0]} <a href="${instagramUrl}" target="_blank" rel="noopener">@${parts[1]}</a>`;
    } else {
      targetNode.innerHTML = `<a href="${instagramUrl}" target="_blank" rel="noopener">${originalText}</a>`;
    }
  }
};

const isCollectionLikePage = () =>
  document.body?.classList.contains('page--collection') ||
  document.body?.classList.contains('page--search');

// Use MutationObserver to handle dynamic loading by the app
const instaObserver = new MutationObserver((mutations, obs) => {
  const title = document.querySelector('.Vtl-InstagramFeedWidget__Title h2');
  if (title) {
    updateInstaFeedLink();
    obs.disconnect();
  }
});

instaObserver.observe(document.body, {
  childList: true,
  subtree: true
});

const CAD_AUTH_GENERIC_ERROR_PATTERN =
  /^there was an error with your submission\.?\s*please try again\.?$/i;
const CAD_AUTH_GENERIC_ERROR_MESSAGE =
  'Please check your details and try again.';
const normalizeText = value => (value ? value.replace(/\s+/g, ' ').trim() : '');

const syncCadLoginCopy = () => {
  const authRootSelector = "cad-overlay.open[data-type='favorites']";

  const loginForm = document.querySelector(`${authRootSelector} #loginUI2`);

  if (loginForm) {
    const submitLabel = loginForm.querySelector('.cad-cta span');
    if (submitLabel && normalizeText(submitLabel.textContent) !== 'LOGIN') {
      submitLabel.textContent = 'LOGIN';
    }

    const createAccountLink = loginForm.querySelector('a.create-account');
    if (createAccountLink instanceof HTMLElement) {
      const signature = 'dont-have-account-sign-up';
      if (createAccountLink.dataset.cadLabelSignature !== signature) {
        const accent = document.createElement('span');
        accent.textContent = 'Sign Up';

        createAccountLink.replaceChildren(
          document.createTextNode('Dont have an account? '),
          accent
        );
        createAccountLink.dataset.cadLabelSignature = signature;
      }
    }
  }

  const authErrorNodes = document.querySelectorAll(
    `${authRootSelector} :is(.info, .error, .error-message)`
  );

  authErrorNodes.forEach(node => {
    if (!(node instanceof HTMLElement)) return;
    const currentText = normalizeText(node.textContent);
    if (!CAD_AUTH_GENERIC_ERROR_PATTERN.test(currentText)) return;
    if (currentText === CAD_AUTH_GENERIC_ERROR_MESSAGE) return;
    node.textContent = CAD_AUTH_GENERIC_ERROR_MESSAGE;
  });

  const getProviderKey = element => {
    if (!(element instanceof HTMLAnchorElement)) return '';

    try {
      const provider = normalizeText(
        new URL(element.href, window.location.origin).searchParams.get('social')
      ).toLowerCase();

      if (provider) return provider;
    } catch (_error) {}

    const iconClass = Array.from(
      element.querySelector('.cad-social-login__button')?.classList || []
    ).find(token => token.endsWith('-button'));

    if (!iconClass) return '';

    return iconClass.replace(/-button$/, '').toLowerCase();
  };

  const getProviderName = providerKey => {
    const key = normalizeText(providerKey).toLowerCase();

    const map = {
      google: 'Google',
      apple: 'Apple',
      twitter: 'X',
      x: 'X',
      facebook: 'Facebook',
      linkedin: 'LinkedIn',
      github: 'GitHub',
      microsoft: 'Microsoft',
      amazon: 'Amazon',
      shopify: 'Shopify'
    };

    if (map[key]) return map[key];
    if (!key) return 'Provider';

    return key
      .replace(/[_-]+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const socialLinks = document.querySelectorAll(
    `${authRootSelector} cad-social-login .cad-social-login__container--element`
  );

  socialLinks.forEach(link => {
    const providerKey = getProviderKey(link);
    const providerName = getProviderName(providerKey);
    const text = `Continue with ${providerName}`;
    const icon = link.querySelector('.cad-social-login__button');

    let label = link.querySelector('.cad-social-login__label');
    if (!label) {
      label = document.createElement('span');
      label.className = 'cad-social-login__label';
    }

    if (label.textContent !== text) {
      label.textContent = text;
    }

    if (icon) {
      icon.setAttribute('aria-hidden', 'true');
      if (icon.nextElementSibling !== label) {
        icon.insertAdjacentElement('afterend', label);
      }
    } else if (label.parentElement !== link) {
      link.appendChild(label);
    }

    link.dataset.cadSocialProvider = providerKey || 'provider';
    link.setAttribute('aria-label', text);
  });
};

// Observer for when cad-overlay[data-type='favorites'] is added to DOM
const cadOverlayWatcher = new MutationObserver(mutations => {
  mutations.forEach(mutation => {
    // Check if new nodes were added
    if (mutation.type === 'childList') {
      mutation.addedNodes.forEach(node => {
        if (
          node instanceof HTMLElement &&
          node.tagName === 'CAD-OVERLAY' &&
          node.dataset.type === 'favorites'
        ) {
          // Found the overlay element, now start observing it
          observeCadOverlay(node);
          // Once found, we can stop watching for new nodes
          cadOverlayWatcher.disconnect();
        }
      });
    }
  });
});

// Start watching the body for when cad-overlay is added
cadOverlayWatcher.observe(document.body, {
  childList: true,
  subtree: true
});

// Observer for the .open class on the overlay
let cadOverlayObserver = null;

const observeCadOverlay = cadOverlayElement => {
  cadOverlayObserver = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'class'
      ) {
        const target = mutation.target;
        if (
          target instanceof HTMLElement &&
          target.classList.contains('open')
        ) {
          syncCadLoginCopy();
        }
      }
    });
  });

  cadOverlayObserver.observe(cadOverlayElement, {
    attributes: true,
    attributeFilter: ['class']
  });
};

// Also check if element already exists (in case script loads after element is added)
const existingCadOverlay = document.querySelector(
  "cad-overlay[data-type='favorites']"
);
if (existingCadOverlay) {
  observeCadOverlay(existingCadOverlay);
  cadOverlayWatcher.disconnect();
}

const IMAGE_ALT_MAX_LENGTH = 125;

const normalizeImageText = value =>
  value ? value.replace(/\s+/g, ' ').trim() : '';

const truncateAltText = value => {
  const text = normalizeImageText(value);

  if (!text) return '';
  if (text.length <= IMAGE_ALT_MAX_LENGTH) return text;

  return text.slice(0, IMAGE_ALT_MAX_LENGTH).trim();
};

const getBaseImageText = () => {
  const heading = document.querySelector('main h1, h1');
  if (heading) {
    const headingText = normalizeImageText(heading.textContent);
    if (headingText) return headingText;
  }

  const title = normalizeImageText(document.title);
  if (title) {
    return normalizeImageText(title.split('|')[0]);
  }

  return 'Product image';
};

const getGalleryPhotoIndex = image => {
  const thumbnailButton = image.closest('.product-gallery__thumbnail');
  if (thumbnailButton) {
    const position = Number.parseInt(
      thumbnailButton.getAttribute('data-media-position') || '',
      10
    );

    if (Number.isFinite(position) && position > 0) return position;
  }

  const mediaSlide = image.closest('.product-gallery__media');
  if (mediaSlide) {
    const position = Number.parseInt(
      mediaSlide.getAttribute('data-media-position') || '',
      10
    );

    if (Number.isFinite(position) && position > 0) return position;

    const siblings = Array.from(
      mediaSlide.parentElement?.querySelectorAll('.product-gallery__media') ||
        []
    );
    const positionFromList = siblings.indexOf(mediaSlide);

    if (positionFromList >= 0) return positionFromList + 1;
  }

  return null;
};

const syncImageAccessibilityAttributes = image => {
  if (!(image instanceof HTMLImageElement)) return;
  if (image.closest('[aria-hidden="true"]')) return;

  const baseImageText = getBaseImageText();
  const hasAltAttribute = image.hasAttribute('alt');
  const currentAlt = normalizeImageText(image.getAttribute('alt'));
  const nextAlt = currentAlt || truncateAltText(baseImageText);

  // Preserve explicit empty alt attributes for decorative images.
  if (!hasAltAttribute && nextAlt) {
    image.setAttribute('alt', nextAlt);
  }

  const currentTitle = normalizeImageText(image.getAttribute('title'));
  if (!currentTitle) {
    const photoIndex = getGalleryPhotoIndex(image);
    const titleBase =
      normalizeImageText(image.getAttribute('alt')) || baseImageText;

    if (photoIndex) {
      image.setAttribute('title', `${titleBase} - photo ${photoIndex}`);
    } else if (titleBase) {
      image.setAttribute('title', titleBase);
    }
  }

  if (!image.hasAttribute('width') && image.naturalWidth > 0) {
    image.setAttribute('width', String(image.naturalWidth));
  }

  if (!image.hasAttribute('height') && image.naturalHeight > 0) {
    image.setAttribute('height', String(image.naturalHeight));
  }
};

const syncAllImagesAccessibilityAttributes = root => {
  root.querySelectorAll('img').forEach(syncImageAccessibilityAttributes);
};

const startImageAccessibilitySync = () => {
  if (isCollectionLikePage()) {
    return;
  }

  syncAllImagesAccessibilityAttributes(document);

  const imageMutationObserver = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(addedNode => {
        if (!(addedNode instanceof Element)) return;

        if (addedNode instanceof HTMLImageElement) {
          syncImageAccessibilityAttributes(addedNode);
        } else {
          syncAllImagesAccessibilityAttributes(addedNode);
        }
      });
    });
  });

  imageMutationObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startImageAccessibilitySync, {
    once: true
  });
} else {
  startImageAccessibilitySync();
}
